// Operator mutation retry intent — a user may retry after a response-loss
// failure without knowing whether the server committed. Keep the same key only
// for the same canonical payload; callers clear the saved intent on success.

import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router";

import { EnvelopeError, SYNTHETIC_ERROR_CODES } from "@/api/envelope";

export interface RetryableSubmission {
  readonly payloadFingerprint: string;
  readonly idempotencyKey: string;
}

/** A durable modal submission owns the exact canonical payload alongside its
 * idempotency key. The clone prevents later form-state changes from minting a
 * second durable initiation after an unknown outcome. */
export interface FrozenRetryableSubmission<
  TPayload,
> extends RetryableSubmission {
  readonly payload: TPayload;
}

/** Browser-safe counterpart of platform-graph's canonical request hashing.
 * Object-key order is presentation-only; array order remains meaningful. */
export function canonicalJson(value: unknown): string {
  const serialized = JSON.stringify(value, (_key, candidate: unknown) => {
    if (
      candidate !== null &&
      typeof candidate === "object" &&
      !Array.isArray(candidate)
    ) {
      const object = candidate as Record<string, unknown>;
      return Object.keys(object)
        .sort()
        .reduce<Record<string, unknown>>((result, key) => {
          result[key] = object[key];
          return result;
        }, {});
    }
    return candidate;
  });
  if (serialized === undefined) {
    throw new Error("idempotency payload must be JSON-serializable");
  }
  return serialized;
}

/** Reuse an in-memory key only for an unchanged logical submission. */
export function resolveRetryableSubmission(
  previous: RetryableSubmission | null,
  payload: unknown,
): RetryableSubmission {
  const payloadFingerprint = canonicalJson(payload);
  if (previous?.payloadFingerprint === payloadFingerprint) return previous;
  return { payloadFingerprint, idempotencyKey: crypto.randomUUID() };
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object") {
    for (const child of Object.values(value)) {
      deepFreeze(child);
    }
    Object.freeze(value);
  }
  return value;
}

/** Snapshot a JSON request body in canonical form before its first durable
 * initiation. Every retry must send this exact immutable payload and key. */
export function freezeRetryableSubmission<TPayload>(
  payload: TPayload,
): FrozenRetryableSubmission<TPayload> {
  const payloadFingerprint = canonicalJson(payload);
  return {
    payload: deepFreeze(JSON.parse(payloadFingerprint) as TPayload),
    payloadFingerprint,
    idempotencyKey: crypto.randomUUID(),
  };
}

/** A 4xx envelope is a definitive rejection: the server did not accept the
 * logical initiation, so the operator may correct the form and mint a new key.
 * Network, malformed, and 5xx outcomes remain unknown and must retain it. */
export function isDefinitiveRetryFailure(error: unknown): boolean {
  return (
    error instanceof EnvelopeError &&
    error.httpStatus >= 400 &&
    error.httpStatus < 500
  );
}

/** A server-written 5xx envelope: the request was answered, but it may answer
 * the same way for a long time (a module switched off). A proxy page without an
 * envelope arrives as the synthetic network code and stays an unknown outcome. */
function isAnsweredServerFailure(error: unknown): boolean {
  return (
    error instanceof EnvelopeError &&
    error.httpStatus >= 500 &&
    !error.hasCode(SYNTHETIC_ERROR_CODES.NETWORK) &&
    !error.hasCode(SYNTHETIC_ERROR_CODES.INVARIANT)
  );
}

/** Own an immutable durable-modal intent for its mounted lifetime. While
 * `locked`, the modal cannot close and SPA navigation stays blocked: the exact
 * request is in flight or its outcome is unknown. A definitive 4xx drops the
 * intent. An answered 5xx keeps it for a same-key retry but unlocks, so the
 * operator can leave; `resume()` locks again for that retry. */
export function useFrozenRetryableSubmission<TPayload>(): {
  readonly current: () => FrozenRetryableSubmission<TPayload> | null;
  readonly resume: () => FrozenRetryableSubmission<TPayload> | null;
  readonly reserve: (payload: TPayload) => FrozenRetryableSubmission<TPayload>;
  readonly settleFailure: (error: unknown) => void;
  readonly clear: () => void;
  readonly hasPendingSubmission: boolean;
  readonly locked: boolean;
} {
  const submissionRef = useRef<FrozenRetryableSubmission<TPayload> | null>(
    null,
  );
  const [hasPendingSubmission, setHasPendingSubmission] = useState(false);
  const [answered, setAnswered] = useState(false);
  const locked = hasPendingSubmission && !answered;
  const navigationBlocker = useBlocker(locked);

  useEffect(() => {
    if (!locked && navigationBlocker.state === "blocked") {
      navigationBlocker.reset();
    }
  }, [locked, navigationBlocker]);

  const current = useCallback(
    (): FrozenRetryableSubmission<TPayload> | null => submissionRef.current,
    [],
  );

  const resume = useCallback((): FrozenRetryableSubmission<TPayload> | null => {
    if (submissionRef.current !== null) setAnswered(false);
    return submissionRef.current;
  }, []);

  const reserve = useCallback(
    (payload: TPayload): FrozenRetryableSubmission<TPayload> => {
      const existing = submissionRef.current;
      if (existing !== null) return existing;
      const next = freezeRetryableSubmission(payload);
      submissionRef.current = next;
      setHasPendingSubmission(true);
      setAnswered(false);
      return next;
    },
    [],
  );

  const clear = useCallback((): void => {
    submissionRef.current = null;
    setHasPendingSubmission(false);
    setAnswered(false);
  }, []);

  const settleFailure = useCallback(
    (error: unknown): void => {
      if (isDefinitiveRetryFailure(error)) clear();
      else if (isAnsweredServerFailure(error)) setAnswered(true);
    },
    [clear],
  );

  return {
    current,
    resume,
    reserve,
    settleFailure,
    clear,
    hasPendingSubmission,
    locked,
  };
}
